import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';

const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword, requestPasswordReset } = useAuth();
  const [loading, setLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('request'); // 'request' or 'reset'

  const token = searchParams.get('token');

  // Form states
  const [requestForm, setRequestForm] = useState({ email: '' });
  const [resetForm, setResetForm] = useState({ 
    password: '', 
    confirmPassword: '' 
  });

  useEffect(() => {
    if (token) {
      setMode('reset');
    }
  }, [token]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!requestForm.email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setRequestLoading(true);
      setError(null);
      
      await requestPasswordReset(requestForm.email);
      toast.success('Password reset email sent! Please check your inbox.');
      
    } catch (error) {
      console.error('Password reset request failed:', error);
      
      if (error.response?.data?.requiresGoogle) {
        toast.error('This account uses Google login only. Please sign in with Google.');
      } else {
        toast.error('Failed to send password reset email. Please try again.');
      }
    } finally {
      setRequestLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetForm.password) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (resetForm.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    
    if (resetForm.password !== resetForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await resetPassword(token, resetForm.password);
      setResetComplete(true);
      toast.success('Password reset successfully!');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Password reset failed:', error);
      
      if (error.response?.data?.expired) {
        setError('The reset link has expired. Please request a new one.');
      } else {
        setError('Invalid or expired reset link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-12 px-6 flex items-center justify-center">
      <div className="w-full max-w-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <img src="/6.png" alt="SignPact Logo" className="h-12 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {mode === 'reset' ? 'Reset Password' : 'Reset Your Password'}
            </h1>
            <p className="text-gray-600">
              {mode === 'reset' 
                ? 'Enter your new password below' 
                : 'Enter your email address and we\'ll send you a reset link'
              }
            </p>
          </div>

          {/* Request Password Reset Form */}
          {mode === 'request' && !resetComplete && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={requestForm.email}
                  onChange={(e) => setRequestForm({ email: e.target.value })}
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={requestLoading}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requestLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2 inline-block"></div>
                    Sending Reset Email...
                  </>
                ) : (
                  'Send Reset Email'
                )}
              </button>

              <div className="text-center pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  Remember your password?{' '}
                  <Link 
                    to="/login" 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === 'reset' && !resetComplete && !error && (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={resetForm.password}
                  onChange={(e) => setResetForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter new password (6+ characters)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={resetForm.confirmPassword}
                  onChange={(e) => setResetForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="Confirm new password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2 inline-block"></div>
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}

          {/* Success State */}
          {resetComplete && (
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Password Reset Complete!
              </h2>
              <p className="text-gray-600 mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              
              <Link
                to="/login"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </Link>
            </div>
          )}

          {/* Error State */}
          {error && mode === 'reset' && (
            <div className="text-center">
              <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Reset Failed
              </h2>
              <p className="text-gray-600 mb-6">
                {error}
              </p>
              
              <div className="space-y-3">
                <Link
                  to="/reset-password"
                  className="block w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-center"
                >
                  Request New Reset Link
                </Link>
                
                <Link
                  to="/login"
                  className="block text-center text-blue-600 hover:text-blue-800"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PasswordResetPage;